import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject, type WheelEvent as ReactWheelEvent } from 'react';

import { clampView, panView, zoomView, type TimeView } from './analysis.js';

/** Common props of the waveform and spectrogram views. */
export interface AudioViewProps {
  left: Float32Array | null | undefined;
  right?: Float32Array | null;
  rate?: number;
  /** Visible range (seconds); controlled when given together with onViewChange (sync with the timeline). */
  view?: TimeView;
  onViewChange?: (view: TimeView) => void;
  /** Playhead (seconds). */
  playhead?: number | null;
  /** Click to seek (seconds). */
  onSeek?: (t: number) => void;
  /** Engine analysis object (onsets, pitch track, centroid…). */
  analysis?: unknown;
  height?: number;
  className?: string;
}

/** Controlled-or-internal time view over `0..duration`. */
export function useTimeView(duration: number, view?: TimeView, onViewChange?: (v: TimeView) => void) {
  const [inner, setInner] = useState<TimeView>({ start: 0, end: duration || 1 });
  const lastDur = useRef(duration);
  useEffect(() => {
    // New render with another length: show everything again.
    if (lastDur.current !== duration) {
      lastDur.current = duration;
      setInner({ start: 0, end: duration || 1 });
    }
  }, [duration]);
  const current = clampView(view ?? inner, duration || 1);
  const set = useCallback(
    (v: TimeView) => {
      const c = clampView(v, duration || 1);
      if (onViewChange) onViewChange(c);
      if (!view) setInner(c);
    },
    [duration, onViewChange, view],
  );
  return [current, set] as const;
}

/** Tracks an element's CSS size and the device pixel ratio. */
export function useElementSize(ref: RefObject<HTMLElement | null>): { width: number; height: number; dpr: number } {
  const [size, setSize] = useState({ width: 0, height: 0, dpr: 1 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setSize({ width: Math.round(r.width), height: Math.round(r.height), dpr: window.devicePixelRatio || 1 });
    };
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return size;
}

/**
 * Pointer/wheel interaction shared by the views: wheel zooms around the
 * pointer (Shift+wheel or horizontal wheel scrolls), drag pans, a click
 * without movement seeks.
 */
export function useViewGestures(
  view: TimeView,
  setView: (v: TimeView) => void,
  duration: number,
  width: number,
  onSeek?: (t: number) => void,
) {
  const drag = useRef<{ x: number; view: TimeView; moved: boolean; id: number } | null>(null);
  const timeAt = (clientX: number, rect: DOMRect) => view.start + ((clientX - rect.left) / Math.max(1, rect.width)) * (view.end - view.start);

  const onWheel = (e: ReactWheelEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const span = view.end - view.start;
    if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      const d = (e.shiftKey ? e.deltaY : e.deltaX) || e.deltaY;
      setView(panView(view, (d / Math.max(1, rect.width)) * span, duration));
    } else {
      setView(zoomView(view, Math.pow(1.0015, e.deltaY), timeAt(e.clientX, rect), duration));
    }
  };
  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, view, moved: false, id: e.pointerId };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 3) d.moved = true;
    if (!d.moved) return;
    const span = d.view.end - d.view.start;
    setView(panView(d.view, (-dx / Math.max(1, width)) * span, duration));
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    if (!d.moved && onSeek) onSeek(Math.max(0, timeAt(e.clientX, e.currentTarget.getBoundingClientRect())));
  };
  return { onWheel, onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp };
}

/** Playhead line over a view (a DOM overlay so moving it never repaints the canvas). */
export function Playhead({ t, view }: { t: number | null | undefined; view: TimeView }) {
  if (t == null || t < view.start || t > view.end || view.end <= view.start) return null;
  return <div className="snd-playhead" style={{ left: `${((t - view.start) / (view.end - view.start)) * 100}%` }} />;
}
